import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ComponentBase } from '../../_infrastructure/classes/componentBase';
import { ExceptionManager } from '../../_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from '../../_infrastructure/classes/services/auth.service';
import { NotificationService } from '../../_infrastructure/classes/services/notification.service';
import { DocumentUploaderService } from './document-uploader.service';
import { DocumentModule, UploadedFileViewModel, ValidFileTypes } from './document_uploader.types';

@Component({
    selector: 'app-document-uploader',
    templateUrl: './document-uploader.component.html',
    styleUrls: ['./document-uploader.component.scss'],
})
export class DocumentUploaderComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() parentForm: UntypedFormGroup;
    @Input() parentSubmitted: boolean;
    @Input() uploadedFile: UploadedFileViewModel;
    @Input() readOnly = false;
    @Input() readOnlyDownload = false;
    @Input() megaSizeLimit: number;
    @Input() isDocumentoDescargable = true; //si documento viene de Traces y es Solicitante no visualizar botón de descarga
    @Input() inputId = 'NombreArchivo';
    @Input() validTypes: string[];
    @Input() documentModule: DocumentModule;
    @Input() required = true;

    @Output() fileUploaded: EventEmitter<UploadedFileViewModel> = new EventEmitter<UploadedFileViewModel>();
    @Output() fileDeleted: EventEmitter<UploadedFileViewModel> = new EventEmitter<UploadedFileViewModel>();

    fileToUpload: File;
    isLoadDisabled = true;
    showFileBrowser = false;
    value = 0;
    contenidoPlaceHolder: string;
    defaultValidTypes = Object.values(ValidFileTypes);

    documentModules = DocumentModule;

    MAXNAMELENGTH = 150;

    get sizeLimit() {
        return this.megaSizeLimit * 1024 * 1024;
    }

    get typesValid() {
        if (this.validTypes?.length > 0) return this.validTypes;
        return this.defaultValidTypes;
    }

    get validTypesToString(): string {
        if (this.validTypes?.length > 0) return this.validTypes.toString();
        return this.defaultValidTypes.toString();
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private uploaderService: DocumentUploaderService,
        protected confirmationService: ConfirmationService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.form = this.parentForm;
        this.resetProgressBar();
        this.contenidoPlaceHolder = this.getContentPlaceHolder();

        if (this.form.value[this.inputId] !== '' && this.form.value[this.inputId] !== null) {
            this.showGridInfoFile();
        } else {
            this.addBrowserValidator();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.uploadedFile?.currentValue) {
            this.checkFileBrowser();
        }
    }

    private addBrowserValidator(): void {
        this.checkFileBrowser();
        if (this.showFileBrowser) {
            this.removeValidators(this.inputId);
            if (this.required) {
                this.addValidator(this.inputId, Validators.required);
            }
        }
    }

    getContentPlaceHolder(): string {
        return this.validTypesToString + '      (' + this.megaSizeLimit.toString() + ' MB máximo) ';
    }

    private removeBrowserValidator(): void {
        this.removeValidators(this.inputId);
    }

    isFieldInvalidRequiredUploader(): boolean | null {
        return this.isFieldInvalid(this.inputId, 'required', this.parentSubmitted);
    }

    isFieldInvalidSizeLimitServerUploader(): boolean | null {
        return this.isFieldInvalid(this.inputId, 'size_limit', this.parentSubmitted);
    }

    isFieldInvalidExtensionUploader(): boolean | null {
        return this.isFieldInvalid(this.inputId, 'extension_invalid', this.parentSubmitted);
    }

    displayFieldCssUploader() {
        return this.displayFieldCss(this.inputId, this.parentSubmitted);
    }

    handleFileInput(files: FileList): void {
        this.fileToUpload = files!.item(0)!;

        if (this.fileToUpload!) {
            this.form.patchValue({
                [this.inputId]: this.fileToUpload!.name,
            });

            if (this.fileToUpload!.size > this.sizeLimit) {
                this.setCustomError('size_limit', this.inputId);
                this.form.controls[this.inputId].markAsTouched();

                return;
            }

            let extension = this.fileToUpload!.name.substring(this.fileToUpload!.name.lastIndexOf('.')).toLowerCase();

            if (!this.typesValid.includes(extension)) {
                this.setCustomError('extension_invalid', this.inputId);
                this.form.controls[this.inputId].markAsTouched();

                return;
            }
        }

        this.isLoadDisabled = false;
        this.cdr.detectChanges();
    }

    onUpload(): void {
        this.isLoadDisabled = true;
        if (!this.uploadedFile) {
            this.uploadedFile = new UploadedFileViewModel(); // Si antes no había adjunto, lo creamos
        }
        this.uploaderService.upload(this.fileToUpload, this.megaSizeLimit, this.documentModule).subscribe({
            next: res => {
                const fileName = this.getTruncatedFilename(this.fileToUpload!.name);
                switch (res.status) {
                    case 'progress':
                        this.uploadedFile.Progress = res.message;
                        break;
                    case 'response':
                        this.uploadedFile.Name = fileName;

                        if (this.uploadedFile!.Guid !== undefined && this.uploadedFile!.Guid !== '') {
                            this.uploadedFile.OldGuidCollection?.push(this.uploadedFile!.Guid);
                        }
                        this.uploadedFile.Guid = res.message;
                        this.uploadedFile.Size = this.fileToUpload.size;
                        this.uploadedFile.MarkToDelete = false;
                        this.form.patchValue({
                            [this.inputId]: fileName,
                        });
                        this.form.markAsDirty();
                        this.resetProgressBar();
                        this.fileUploaded.emit(this.uploadedFile);
                        this.checkFileBrowser();
                        break;
                    default:
                        break;
                }
            },
            error: error => {
                this.form.controls[this.inputId].markAsTouched();
                if (error?.error?.length > 0) {
                    error.error[0].field = this.inputId;
                }
                this.catchServerError(error);
                this.resetProgressBar();
            },
        });
    }

    /**
     * This method removes the spare characters from the original name if its lenght is greater than 150.
     * The spare lenght will be those extra characteres above the maximal length.
     * But they will be removed before the position of the extension of the file.
     * Example:
     *   - filename = 'archivo-con-nombre-superior-150-caracteres-0123456789-0123456789-1123456789-2123456789-3123456789-4123456789-5123456789-6123456789-7123456789-8123456789.pdf';
     *   - filename lenght will be: 156
     *   - lastPointIndex will be: 152
     *   - spareLenght will be: 6
     *   - truncated filename will be: 'archivo-con-nombre-superior-150-caracteres-0123456789-0123456789-1123456789-2123456789-3123456789-4123456789-5123456789-6123456789-7123456789-8123.pdf'
     * @param filename string
     * @returns string
     */
    private getTruncatedFilename(filename: string): string {
        const lastPointIndex = filename.lastIndexOf('.');
        const spareLength = filename.length - this.MAXNAMELENGTH;
        return filename.length > this.MAXNAMELENGTH
            ? filename.slice(0, lastPointIndex - spareLength) + filename.slice(lastPointIndex, filename.length)
            : filename;
    }

    onDownload(): void {
        this.uploaderService.download(this.uploadedFile, this.documentModule).subscribe(res => {
            this.OpenDownloadedFile(res);
        });
    }

    onDelete(): void {
        this.form.patchValue({
            [this.inputId]: '',
        });

        this.uploadedFile.MarkToDelete = true;
        this.addBrowserValidator();
        this.fileDeleted.emit(this.uploadedFile);
    }

    onCleanButton(): void {
        this.form.patchValue({
            [this.inputId]: '',
        });
        this.isLoadDisabled = true;
    }

    onCancel(): void {
        if (this.uploadedFile) {
            this.uploadedFile.MarkToDelete = false;

            this.form.patchValue({
                [this.inputId]: this.fileToUpload?.name,
            });

            this.isLoadDisabled = true;

            this.removeBrowserValidator();
            this.checkFileBrowser();

            this.fileDeleted.emit(this.uploadedFile); // The difference is now MarkToDelete is false
        }
    }

    getSizeKb(): number | null {
        //return Math.round(this.uploadedFile.Size / 1024) || null;
        if (!this.uploadedFile || !this.uploadedFile.Size) return null;
        const sizeKb = this.uploadedFile.Size / 1024;
        return Number.isInteger(sizeKb) ? sizeKb : parseFloat(sizeKb.toFixed(2));
    }

    private resetProgressBar(): void {
        if (!this.uploadedFile) return;
        this.uploadedFile.Progress = 0;
    }

    private checkFileBrowser(): void {
        if (this.readOnly) {
            this.showFileBrowser = false;
            return;
        }

        if (!this.uploadedFile || !this.uploadedFile.Guid || this.uploadedFile!.MarkToDelete) {
            this.showFileBrowser = true;
            return;
        }

        this.showFileBrowser = false;
    }

    private showGridInfoFile(): void {
        this.checkFileBrowser();
        if (!this.uploadedFile) {
            return;
        }
        this.uploadedFile.Name =
            this.form.value[this.inputId] !== undefined && this.form.value[this.inputId] !== ''
                ? this.form.value[this.inputId]
                : this.uploadedFile.Name !== ''
                  ? this.uploadedFile.Name
                  : '';
        //this.uploadedFile.Guid = this.form.value.Guid;
        //this.uploadedFile.Size = this.form.value.Size;
    }

    showCancelButton(): boolean {
        if (this.uploadedFile) {
            return this.uploadedFile.MarkToDelete || false;
        }
        return false;
    }

    get fileTitle(): string {
        switch (this.documentModule) {
            case DocumentModule.Campania:
                return 'documento';
            case DocumentModule.Gestor:
                return 'documento';
            default:
                return 'archivo';
        }
    }

    get deleteTitle(): string {
        switch (this.documentModule) {
            case this.documentModules.Default:
                return 'Cambiar ' + this.fileTitle;
            default:
                return 'Eliminar/Cambiar' + this.fileTitle;
        }
    }
}
